import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Store, Coins, X, Check, Package, ShoppingBag, Globe, Crown, Trophy, Shield,
  Sparkles, BadgeCheck, Square, Award, Lock, Gift, Ticket, Star, Gem, Boxes,
} from "lucide-react";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Page, PageTitle, GoldButton, GhostButton, Loader } from "../components/UI";
import { Flag } from "../components/Flag";

const ICONS = { Globe, Crown, Trophy, Shield, Sparkles, BadgeCheck, Square, Award, Package, Store, Gift, Ticket, Star };
const RARITY = {
  legendary: { label: "Legendary", ring: "ring-[#E5B842]/70", text: "text-[#E5B842]", glow: "rgba(229,184,66,0.22)" },
  epic: { label: "Epic", ring: "ring-[#C99E30]/60", text: "text-[#d9b24e]", glow: "rgba(201,158,48,0.16)" },
  rare: { label: "Rare", ring: "ring-[#5b74a6]/60", text: "text-[#9fb4d9]", glow: "rgba(91,116,166,0.16)" },
  common: { label: "Common", ring: "ring-[#3a4150]/70", text: "text-slate-300", glow: "rgba(100,110,130,0.12)" },
};
const FLAGS = { GRC: "gr", CYP: "cy" };
const TYPE_LABEL = { collectible_card: "Card", cosmetic: "Cosmetic", pack: "Pack", functional: "Unlock" };
const FILTERS = [
  { key: "all", label: "All" },
  { key: "collectible_card", label: "Cards" },
  { key: "cosmetic", label: "Cosmetics" },
  { key: "pack", label: "Packs" },
  { key: "functional", label: "Unlocks" },
];

function ItemVisual({ it, size = "lg" }) {
  const Icon = ICONS[it.icon] || Package;
  const accent = it.accent || "#E5B842";
  const big = size === "lg";
  return (
    <div
      className={`relative overflow-hidden rounded-xl grid place-items-center ${big ? "h-40" : "h-28"}`}
      style={{ background: `radial-gradient(120% 120% at 50% 0%, ${accent}22 0%, #0a0a0a 60%), #070707` }}
    >
      <div className="absolute inset-0 mesh-grid opacity-[0.18] pointer-events-none" />
      {it.flag && FLAGS[it.flag] && <div className="absolute opacity-20" aria-hidden="true"><Flag country={{ iso2: FLAGS[it.flag] }} width={big ? 90 : 56} /></div>}
      {it.tier && <div className="absolute top-2 left-2 text-[9px] font-mono uppercase tracking-wider text-slate-400 bg-black/50 px-1.5 py-0.5 rounded">Tier {it.tier}</div>}
      <Icon className={`${big ? "w-14 h-14" : "w-9 h-9"} relative`} style={{ color: accent }} strokeWidth={1.5} />
    </div>
  );
}

function ItemCard({ it, onOpen }) {
  const r = RARITY[it.rarity] || RARITY.common;
  return (
    <button
      data-testid={`shop-item-${it.sku}`}
      onClick={() => onOpen(it)}
      className={`group text-left rounded-2xl bg-[#0d0d0d] border border-[#1e1e1e] p-3 ring-1 ${r.ring} hover:border-[#E5B842]/40`}
      style={{ transition: "border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = `0 16px 40px -18px ${r.glow}`; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
    >
      <ItemVisual it={it} />
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className={`text-[9px] font-mono uppercase tracking-[0.18em] ${r.text}`}>{r.label}</span>
        <span className="text-[9px] font-mono uppercase tracking-wider text-slate-500">
          {TYPE_LABEL[it.type] || it.type}
        </span>
      </div>
      <h3 className="mt-1 font-heading text-base font-bold uppercase tracking-tight text-slate-50 leading-tight break-words">{it.name}</h3>
      <div className="mt-2 flex items-center gap-1.5 text-[#E5B842] font-heading font-black tabular-nums">
        <Coins className="w-4 h-4" /> {it.price_vp.toLocaleString()} <span className="text-[10px] font-mono text-slate-500 font-normal">VP</span>
      </div>
    </button>
  );
}

function ItemModal({ it, onClose, balance, owned, onBought }) {
  const { user } = useAuth();
  const r = RARITY[it.rarity] || RARITY.common;
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const canAfford = balance >= it.price_vp;
  const blockOwned = it.own_once && owned;

  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/shop/purchase", { sku: it.sku });
      setDone(data);
      onBought(data);
    } catch (e) {
      toast.error(apiErr(e));
      setConfirming(false);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="shop-item-modal">
      <div className={`w-full max-w-md rounded-2xl bg-[#0b0b0b] border border-[#2b2512] ring-1 ${r.ring} p-6`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <span className={`text-[10px] font-mono uppercase tracking-[0.2em] ${r.text}`}>{r.label} · {TYPE_LABEL[it.type] || it.type}</span>
          <button onClick={onClose} data-testid="shop-modal-close" className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-3"><ItemVisual it={it} /></div>
        <h2 className="mt-4 font-heading text-2xl font-black uppercase tracking-tight text-slate-50 break-words">{it.name}</h2>
        <p className="mt-2 text-sm text-slate-400 leading-snug">{it.description}</p>

        {/* Pack transparency — published odds / exact contents BEFORE buying. */}
        {it.type === "pack" && it.pack_kind === "random" && it.pack_odds && (
          <div className="mt-3 rounded-xl bg-[#0a0a0a] border border-[#2b2512] p-3" data-testid="shop-pack-odds">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">Published odds · {it.pack_rolls} cards · server-rolled</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {Object.entries(it.pack_odds).map(([rar, w]) => (
                <span key={rar} className={`text-xs font-mono ${(RARITY[rar] || RARITY.common).text}`}>{(RARITY[rar] || {}).label || rar}: {w}%</span>
              ))}
            </div>
          </div>
        )}
        {it.type === "pack" && it.pack_kind === "fixed" && it.pack_contents && (
          <div className="mt-3 rounded-xl bg-[#0a0a0a] border border-[#2b2512] p-3" data-testid="shop-pack-contents">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Contains</div>
            <div className="text-xs text-slate-300">{it.pack_contents.join(" · ")}</div>
          </div>
        )}

        <div className="mt-4 flex items-center gap-2 text-[#E5B842] font-heading text-2xl font-black tabular-nums">
          <Coins className="w-5 h-5" /> {it.price_vp.toLocaleString()} <span className="text-xs font-mono text-slate-500 font-normal">VP</span>
        </div>

        {done ? (
          <div className="mt-5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-4 text-center" data-testid="shop-purchase-success">
            <Check className="w-7 h-7 text-emerald-400 mx-auto mb-1.5" />
            <div className="font-heading font-bold uppercase text-emerald-300">Purchase complete</div>
            <div className="text-xs font-mono text-slate-400 mt-1">New balance: {(done.balance ?? balance).toLocaleString()} VP{done.qty > 1 ? ` · owned ×${done.qty}` : ""}</div>
            <Link to="/shop?tab=inventory" onClick={onClose} data-testid="shop-view-items" className="inline-block mt-3"><GoldButton><Package className="w-4 h-4" /> View in My Items</GoldButton></Link>
          </div>
        ) : !user ? (
          <Link to="/login" className="block mt-5"><GoldButton className="w-full" data-testid="shop-login-to-buy"><Lock className="w-4 h-4" /> Sign in to buy</GoldButton></Link>
        ) : blockOwned ? (
          <div className="mt-5 rounded-xl bg-[#141414] border border-[#2b2512] p-3 text-center text-sm text-slate-400" data-testid="shop-owned-note">
            <Check className="w-4 h-4 text-emerald-400 inline mr-1" /> You already own this cosmetic.
          </div>
        ) : !canAfford ? (
          <div className="mt-5" data-testid="shop-insufficient">
            <div className="rounded-xl bg-rose-500/10 border border-rose-500/30 p-3 text-center text-sm text-rose-300">
              Not enough VP — you have <b>{balance.toLocaleString()}</b>, this costs <b>{it.price_vp.toLocaleString()}</b>.
            </div>
          </div>
        ) : confirming ? (
          <div className="mt-5 space-y-2" data-testid="shop-confirm">
            <div className="text-center text-sm text-slate-300 font-mono">Spend <b className="text-[#E5B842]">{it.price_vp.toLocaleString()} VP</b>?</div>
            <div className="flex gap-2">
              <GoldButton className="flex-1" disabled={busy} onClick={buy} data-testid="shop-confirm-buy">{busy ? "Processing…" : "Confirm"}</GoldButton>
              <GhostButton className="flex-1" onClick={() => setConfirming(false)}>Cancel</GhostButton>
            </div>
          </div>
        ) : (
          <GoldButton className="w-full mt-5" onClick={() => setConfirming(true)} data-testid="shop-buy-btn"><ShoppingBag className="w-4 h-4" /> Buy</GoldButton>
        )}
      </div>
    </div>
  );
}

function InventoryGrid({ items, equipped, onOpen, onEquip, busySku }) {
  if (!items.length) return (
    <div className="rounded-xl border border-dashed border-[#2b2512] p-10 text-center" data-testid="inventory-empty">
      <Package className="w-8 h-8 text-slate-700 mx-auto mb-3" />
      <div className="font-heading text-lg font-bold uppercase text-slate-300">No items yet</div>
      <div className="text-sm text-slate-500 mt-1">Browse the shop and start your WSTF collection.</div>
    </div>
  );
  const equippedSkus = new Set(Object.values(equipped || {}));
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="inventory-grid">
      {items.map((row) => {
        const it = row.item; const r = RARITY[it.rarity] || RARITY.common;
        const isEquipped = equippedSkus.has(it.sku);
        return (
          <div key={row.sku} data-testid={`inventory-item-${row.sku}`} className={`relative rounded-2xl bg-[#0d0d0d] border border-[#1e1e1e] p-3 ring-1 ${r.ring} flex flex-col`}>
            {row.qty > 1 && <span className="absolute top-2 right-2 z-10 text-[10px] font-heading font-black text-[#000] bg-[#E5B842] rounded-full px-2 py-0.5" data-testid={`inventory-qty-${row.sku}`}>×{row.qty}</span>}
            <ItemVisual it={it} size="sm" />
            <div className={`mt-2 text-[9px] font-mono uppercase tracking-[0.18em] ${r.text}`}>{r.label} · {TYPE_LABEL[it.type] || it.type}</div>
            <div className="font-heading text-sm font-bold uppercase tracking-tight text-slate-50 leading-tight break-words flex-1">{it.name}</div>
            {it.type === "pack" && (
              <button data-testid={`inventory-open-${row.sku}`} disabled={busySku === row.sku}
                onClick={() => onOpen(row)}
                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E5B842] text-black text-xs font-heading font-bold uppercase tracking-wide hover:brightness-110 disabled:opacity-60">
                <Gift className="w-3.5 h-3.5" /> {busySku === row.sku ? "Opening…" : "Open"}
              </button>
            )}
            {it.type === "cosmetic" && (
              <button data-testid={`inventory-equip-${row.sku}`} disabled={busySku === row.sku}
                onClick={() => onEquip(it, !isEquipped)}
                className={`mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-heading font-bold uppercase tracking-wide ${isEquipped ? "bg-[#161616] text-[#E5B842] ring-1 ring-[#E5B842]/50" : "bg-[#161616] text-slate-300 ring-1 ring-[#2b2512] hover:text-[#E5B842]"} disabled:opacity-60`}>
                {isEquipped ? <><Check className="w-3.5 h-3.5" /> Equipped</> : "Equip"}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PackRevealModal({ reveal, onClose }) {
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center p-4 bg-black/85 backdrop-blur-sm" onClick={onClose} data-testid="pack-reveal-modal">
      <div className="w-full max-w-lg rounded-2xl bg-[#0b0b0b] border border-[#E5B842]/40 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2"><Gift className="w-5 h-5 text-[#E5B842]" /><span className="font-heading text-lg font-black uppercase tracking-tight text-slate-50">{reveal.pack_name} opened</span></div>
          <button onClick={onClose} data-testid="pack-reveal-close" className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-5 grid grid-cols-3 gap-3" data-testid="pack-reveal-cards">
          {reveal.results.map((g, i) => {
            const it = g.item || { name: g.sku, rarity: "common" };
            const r = RARITY[it.rarity] || RARITY.common;
            const Icon = ICONS[it.icon] || Trophy;
            return (
              <div key={i} className={`rounded-xl bg-[#0d0d0d] border border-[#1e1e1e] p-2.5 ring-1 ${r.ring} animate-fade-up`} style={{ animationDelay: `${i * 160}ms` }}>
                <div className="relative h-20 rounded-lg grid place-items-center overflow-hidden" style={{ background: `radial-gradient(120% 120% at 50% 0%, ${it.accent || "#E5B842"}22 0%, #070707 60%)` }}>
                  <Icon className="w-7 h-7" style={{ color: it.accent || "#E5B842" }} strokeWidth={1.5} />
                </div>
                <div className={`mt-1.5 text-[8px] font-mono uppercase tracking-wider ${r.text}`}>{r.label}</div>
                <div className="font-heading text-[11px] font-bold uppercase tracking-tight text-slate-50 leading-tight break-words">{it.name}</div>
                {g.shards ? <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-mono text-[#9fb4d9]"><Gem className="w-3 h-3" /> +{g.shards} shards (dup)</div> : null}
              </div>
            );
          })}
        </div>
        {reveal.shards_awarded > 0 && (
          <div className="mt-4 text-center text-xs font-mono text-[#9fb4d9]" data-testid="pack-reveal-shards">
            Duplicates converted to <b>{reveal.shards_awarded}</b> shards · balance {reveal.shards_balance}
          </div>
        )}
        <GoldButton className="w-full mt-5" onClick={onClose} data-testid="pack-reveal-done"><Check className="w-4 h-4" /> Collect</GoldButton>
      </div>
    </div>
  );
}

export default function ShopPage() {
  const { user, player, refresh } = useAuth();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [balance, setBalance] = useState(0);
  const [shards, setShards] = useState(0);
  const [inv, setInv] = useState(null);
  const [equipped, setEquipped] = useState({});
  const [reveal, setReveal] = useState(null);
  const [busySku, setBusySku] = useState(null);
  const initialTab = new URLSearchParams(window.location.search).get("tab") === "inventory" ? "inventory" : "shop";
  const [tab, setTab] = useState(initialTab);

  useEffect(() => { api.get("/shop/items").then(({ data }) => setItems(data.items)).catch(() => setItems([])); }, []);

  const loadInv = useCallback(() => {
    if (!user) { setInv([]); return; }
    api.get("/me/inventory").then(({ data }) => { setInv(data.items); setShards(data.shards || 0); }).catch(() => setInv([]));
  }, [user]);
  useEffect(() => { loadInv(); }, [loadInv]);

  useEffect(() => {
    if (!user) { setBalance(0); setEquipped({}); return; }
    api.get("/me/wallet").then(({ data }) => { setBalance(data.balance); setShards(data.shards || 0); }).catch(() => {});
    api.get("/auth/me").then(({ data }) => setEquipped(data.player?.equipped || {
      frame: data.player?.equipped_frame, badge: data.player?.equipped_badge,
    })).catch(() => {});
  }, [user, player]);

  const ownedSkus = new Set((inv || []).map((r) => r.sku));
  const shown = (items || []).filter((it) => filter === "all" || it.type === filter);

  const onBought = (data) => {
    if (typeof data.balance === "number") setBalance(data.balance);
    loadInv();
    refresh && refresh();
  };

  const onOpen = async (row) => {
    setBusySku(row.sku);
    try {
      const { data } = await api.post("/shop/open", { sku: row.sku });
      setReveal(data);
      setShards(data.shards_balance || 0);
      loadInv();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusySku(null); }
  };

  const onEquip = async (it, equip) => {
    setBusySku(it.sku);
    try {
      const { data } = await api.post("/me/cosmetics/equip", { sku: it.sku, equip });
      setEquipped(data.equipped || {});
      toast.success(equip ? "Equipped" : "Unequipped");
      refresh && refresh();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusySku(null); }
  };

  if (!items) return <Loader label="Loading shop…" />;

  return (
    <Page>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageTitle eyebrow="WSTF · Virtual Store" title="WSTF Shop" subtitle="Collect cards and cosmetics with Virtual Points. Strictly virtual — never real money." />
        {user && (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.3)] px-4 py-2.5 flex items-center gap-2" data-testid="shop-vp-balance">
              <Coins className="w-5 h-5 text-[#E5B842]" />
              <div>
                <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 leading-none">Your balance</div>
                <div className="font-heading text-xl font-black text-[#E5B842] tabular-nums leading-tight">{balance.toLocaleString()} <span className="text-[10px] font-mono text-slate-500 font-normal">VP</span></div>
              </div>
            </div>
            <div className="rounded-xl bg-[#0f0f0f] border border-[#2b3650] px-4 py-2.5 flex items-center gap-2" data-testid="shop-shards-balance">
              <Gem className="w-5 h-5 text-[#9fb4d9]" />
              <div>
                <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 leading-none">Shards</div>
                <div className="font-heading text-xl font-black text-[#9fb4d9] tabular-nums leading-tight">{shards.toLocaleString()}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-[#0a0a0a] border border-[#2b2512] w-fit mb-5" data-testid="shop-tabs">
        {[{ k: "shop", l: "Shop", I: Store }, { k: "inventory", l: "My Items", I: Boxes }].map(({ k, l, I }) => (
          <button key={k} data-testid={`shop-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-lg text-sm font-heading font-bold uppercase tracking-wide inline-flex items-center gap-2 ${tab === k ? "bg-[#E5B842] text-black" : "text-slate-300 hover:text-[#E5B842]"}`}
            style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>
            <I className="w-4 h-4" /> {l}
          </button>
        ))}
      </div>

      {tab === "shop" ? (
        <>
          <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-[#0a0a0a] border border-[#2b2512] w-fit mb-6" data-testid="shop-filters">
            {FILTERS.map((f) => (
              <button key={f.key} data-testid={`shop-filter-${f.key}`} onClick={() => setFilter(f.key)}
                className={`px-4 py-1.5 rounded-lg text-xs font-heading font-bold uppercase tracking-wide ${filter === f.key ? "bg-[#161616] text-[#E5B842] ring-1 ring-[#E5B842]/40" : "text-slate-400 hover:text-slate-200"}`}
                style={{ transition: "color 0.15s ease" }}>{f.label}</button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="shop-grid">
            {shown.map((it) => <ItemCard key={it.sku} it={it} onOpen={setActive} />)}
          </div>
        </>
      ) : (
        !user ? (
          <Link to="/login"><GoldButton>Sign in to view your items</GoldButton></Link>
        ) : inv === null ? <Loader /> : <InventoryGrid items={inv} equipped={equipped} onOpen={onOpen} onEquip={onEquip} busySku={busySku} />
      )}

      {active && (
        <ItemModal it={active} onClose={() => setActive(null)} balance={balance}
          owned={ownedSkus.has(active.sku)} onBought={onBought} />
      )}
      {reveal && <PackRevealModal reveal={reveal} onClose={() => setReveal(null)} />}
    </Page>
  );
}
